/**
 * AccountWorkspace — Per-account view. Tab driven by ?tab= query param.
 * SecondaryNav handles navigation; this component renders the active section.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, TextField, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { Link as RouterLink, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ACCOUNT_PHASES, PHASE_PLAYBOOKS, SEED_ACCOUNTS } from '../../config/accountPlaybooks';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import KlantDetail from '../Klanten/KlantDetail';
import DatabeleidSection from './DatabeleidSection';
import AccountPriceGuidanceSection from './AccountPriceGuidanceSection';
import PageHeader from '../../ui-components/PageHeader';
import TeamBewerkDialog from './TeamBewerkDialog';
import AccountCijfersSection from './AccountCijfersSection';
import AccountCrmSection from './AccountCrmSection';
import AccountPlanSection from './AccountPlanSection';
import AccountSatisfactionSection from './AccountSatisfactionSection';
import { useUserRole } from '../../contexts/UserRoleContext';
import toast from 'react-hot-toast';
import { testbalkPlus } from '../../utils/testbalk';
import LogoMetTerugval from '../../ui-components/LogoMetTerugval';

const T = {
  primary: '#526075', primaryContainer: '#d5e3fd',
  surface: '#ffffff', surfaceLow: '#f1f4f5',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  tertiary: '#635983', tertiaryContainer: '#dbcdff',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

// Mirror-velden (Postgres-flip) leveren lookups/multipleSelects als ARRAY waar
// Airtable eerder een comma-string gaf — normaliseer vóór elke .split/.trim
// (bekende recurring bug, zie CLAUDE.md "Array vs String Mismatch").
/** Projectlijst gefilterd op één klant/bedrijf (ProjectList leest ?klant=). */
export const klantProjectenLink = (naam, id) => {
  const q = new URLSearchParams({ klant: String(naam || '') });
  if (id) q.set('klantId', String(id));
  return `/projects?${q.toString()}`;
};

const alsTekst = (v) => Array.isArray(v) ? v.filter(Boolean).join(', ') : (v == null ? '' : String(v));

// Strip markdown bold (**text**) from Airtable strings
const stripMd = (s) => typeof s === 'string' ? s.replace(/\*\*/g, '') : s;

// Parse markdown bold (**text**) into React nodes with <b> tags
const parseBold = (s) => {
  if (typeof s !== 'string') return s;
  const parts = s.split(/\*\*(.+?)\*\*/g);
  if (parts.length === 1) return s;
  return parts.map((part, i) => i % 2 === 1 ? <b key={i}>{part}</b> : part);
};

// ─── Playbook Hub ──────────────────────────────────────────────────────────
// Landing voor ?tab=playbook — fase-kaarten in hub-stijl. Klik → fase-prompts.
function PlaybookHub() {
  const navigate = useNavigate();

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 3 }}>
        <Box sx={{ width: 28, height: 28, borderRadius: '0.5rem', bgcolor: T.tertiaryContainer, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="auto_stories" size={16} sx={{ color: T.tertiary }} />
        </Box>
        <Typography sx={{ fontFamily: T.font, fontSize: '0.92rem', fontWeight: 700, color: T.text }}>
          AI Playbook
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
        {ACCOUNT_PHASES.map((phase) => {
          const count = (PHASE_PLAYBOOKS[phase.id] || []).length;
          return (
            <Box
              key={phase.id}
              onClick={() => navigate(`?tab=playbook&phase=${phase.id}`)}
              sx={{
                display: 'flex', flexDirection: 'column', gap: 1.5,
                bgcolor: T.surface, borderRadius: '0.75rem', p: 2.5,
                border: '1px solid rgba(45,51,53,0.06)',
                cursor: 'pointer', transition: 'all 0.15s',
                '&:hover': {
                  boxShadow: '0 8px 24px rgba(45,51,53,0.07)',
                  borderColor: `${phase.color}40`,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '0.625rem', bgcolor: `${phase.color}1f`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name={phase.icon || 'auto_stories'} size={22} sx={{ color: phase.color }} />
                </Box>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 600, color: T.outlineVariant }}>
                  {count} {count === 1 ? 'prompt' : 'prompts'}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.88rem', fontWeight: 700, color: T.text, mb: 0.25 }}>
                  {phase.label}
                </Typography>
                {phase.desc && (
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline, lineHeight: 1.45 }}>
                    {phase.desc}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ─── Playbook Section ──────────────────────────────────────────────────────
function PlaybookSection({ accountName, activePhase }) {
  const navigate = useNavigate();
  const playbooks = PHASE_PLAYBOOKS[activePhase] || [];
  const phaseLabel = ACCOUNT_PHASES.find(p => p.id === activePhase)?.label || activePhase;

  const handlePromptCopy = (prompt) => {
    const filled = prompt.replace(/\{account\}/g, accountName);
    navigator.clipboard.writeText(filled);
  };

  const handlePromptOpen = (prompt) => {
    const filled = prompt.replace(/\{account\}/g, accountName);
    sessionStorage.setItem('ai_hub_prompt', filled);
    navigate('/ai-chat');
  };

  return (
    <Box>
      {/* Phase label */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 3 }}>
        <Box sx={{ width: 28, height: 28, borderRadius: '0.5rem', bgcolor: T.tertiaryContainer, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="auto_stories" size={16} sx={{ color: T.tertiary }} />
        </Box>
        <Typography sx={{ fontFamily: T.font, fontSize: '0.92rem', fontWeight: 700, color: T.text }}>
          {phaseLabel}
        </Typography>
      </Box>

      {playbooks.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Icon name="auto_stories" size={40} sx={{ color: T.outlineVariant, display: 'block', margin: '0 auto 12px' }} />
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.88rem', fontWeight: 600, color: T.text, mb: 0.5 }}>
            Binnenkort beschikbaar
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.outline }}>
            Playbooks voor deze fase worden nog toegevoegd.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {playbooks.map((item, i) => (
            <Box
              key={item.id}
              sx={{
                p: 2, borderRadius: '0.625rem',
                border: '1px solid rgba(45,51,53,0.06)',
                bgcolor: T.surface,
                transition: 'all 0.15s',
                '&:hover': { borderColor: `${T.tertiary}25`, boxShadow: '0 4px 12px rgba(45,51,53,0.05)' },
                '&:hover .ws-actions': { opacity: 1 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.88rem', fontWeight: 600, flex: 1, color: T.text }}>
                  {item.label}
                </Typography>
                <Box className="ws-actions" sx={{ display: 'flex', gap: 1.5, opacity: 0, transition: 'opacity 0.15s' }}>
                  <Typography
                    onClick={() => handlePromptCopy(item.prompt)}
                    sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', color: T.outlineVariant, cursor: 'pointer', '&:hover': { color: T.text }, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.04em' }}
                  >
                    Copy
                  </Typography>
                  <Typography
                    onClick={() => handlePromptOpen(item.prompt)}
                    sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', color: T.primary, cursor: 'pointer', '&:hover': { color: T.text }, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.04em' }}
                  >
                    Open in AI Chat
                  </Typography>
                </Box>
              </Box>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline, mt: 0.5 }}>
                {item.prompt.replace(/\{account\}/g, accountName).split('\n')[0].substring(0, 120)}...
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ─── Klantprofiel Section ───────────────────────────────────────────────────

const PROFILE_FIELDS = [
  { key: 'missie', label: 'Missie', icon: 'flag', placeholder: 'Waar staat deze organisatie voor? Wat is hun bestaansrecht?', rows: 3 },
  { key: 'visie', label: 'Visie', icon: 'visibility', placeholder: 'Waar wil de organisatie naartoe? Wat is het toekomstbeeld?', rows: 3 },
  { key: 'kernwaarden', label: 'Kernwaarden', icon: 'favorite', placeholder: 'Welke waarden zijn leidend? (bijv. innovatief, betrouwbaar, duurzaam)', rows: 2 },
  { key: 'doelgroep', label: 'Doelgroep', icon: 'groups', placeholder: 'Wie wil de klant bereiken? Denk aan leeftijd, functie, sector, behoeften.', rows: 3 },
  { key: 'tone_of_voice', label: 'Tone of Voice', icon: 'record_voice_over', placeholder: 'Hoe communiceert dit merk? Formeel/informeel, speels/zakelijk, warm/zakelijk.', rows: 3 },
  { key: 'usp', label: "Unique Selling Points", icon: 'star', placeholder: 'Wat maakt deze klant uniek? Waarin onderscheiden ze zich van concurrenten?', rows: 3 },
  { key: 'concurrenten', label: 'Concurrenten & Markt', icon: 'analytics', placeholder: 'Wie zijn de belangrijkste concurrenten? Hoe positioneert de klant zich?', rows: 3 },
  { key: 'kernboodschap', label: 'Kernboodschap', icon: 'campaign', placeholder: 'Wat is de centrale boodschap die in alle communicatie terugkomt?', rows: 2 },
  { key: 'do_dont', label: "Do's & Don'ts", icon: 'rule', placeholder: "Wat moet je altijd doen in communicatie voor deze klant? Wat absoluut niet?", rows: 3 },
  { key: 'notities', label: 'Overige notities', icon: 'sticky_note_2', placeholder: 'Andere relevante informatie over deze klant.', rows: 4 },
];

function KlantProfielSection({ accountId }) {
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // AI-voorstel uit publieke bronnen (klantwebsite): vult alléén lege velden
  // in het formulier — opslaan blijft een bewuste klik van de gebruiker.
  const [aiOpen, setAiOpen] = useState(false);
  const [aiWebsite, setAiWebsite] = useState('');
  const [aiBusy, setAiBusy] = useState(false);

  const vulAanMetAI = async () => {
    if (!aiWebsite.trim() || aiBusy) return;
    setAiBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/klanten/profile/afleiden`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ website: aiWebsite.trim() }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const d = await r.json();
      const velden = d.velden || {};
      let ingevuld = 0;
      setProfile(prev => {
        const next = { ...prev };
        for (const [k, v] of Object.entries(velden)) {
          if (!String(next[k] || '').trim()) { next[k] = v; ingevuld += 1; }
        }
        return next;
      });
      setDirty(true);
      setAiOpen(false);
      toast.success(ingevuld
        ? `${ingevuld} veld${ingevuld === 1 ? '' : 'en'} voorgevuld uit ${(d.bronnen || []).length} pagina's — lees na en sla op`
        : 'Geen nieuwe informatie gevonden (gevulde velden blijven staan)');
    } catch (e) {
      toast.error(`Aanvullen mislukt: ${e.message}`);
    } finally {
      setAiBusy(false);
    }
  };

  const loadProfile = useCallback(async () => {
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/klanten/${accountId}/profile`);
      if (res.ok) {
        const data = await res.json();
        setProfile(data.profile || {});
      }
    } catch (err) {
      console.error('Failed to load profile:', err);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const handleChange = (key, value) => {
    setProfile(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };
  // Onopgeslagen tekst niet stil kwijtraken (audit 19 sep: 'Overige notities' verdween bij
  // tabwissel): bij verlaten van het tabblad wordt een openstaande wijziging alsnog opgeslagen,
  // en de browser waarschuwt bij sluiten van het venster.
  const laatste = useRef({ dirty: false, profile: null });
  useEffect(() => { laatste.current = { dirty, profile }; }, [dirty, profile]);
  useEffect(() => () => {
    const { dirty: open, profile: p } = laatste.current;
    if (!open || !p) return;
    authFetch(`${BACKEND_URL}/Api/klanten/profile`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ account_airtable_id: accountId, ...p }),
    }).then((r) => { if (r.ok) toast.success('Klantprofiel automatisch opgeslagen'); else toast.error('Klantprofiel niet opgeslagen — open het tabblad Profiel en klik op Opslaan'); })
      .catch(() => toast.error('Klantprofiel niet opgeslagen — open het tabblad Profiel en klik op Opslaan'));
  }, [accountId]);
  useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/klanten/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_airtable_id: accountId, ...profile }),
      });
      if (res.ok) {
        toast.success('Profiel opgeslagen');
        setDirty(false);
      } else {
        toast.error('Opslaan mislukt');
      }
    } catch {
      toast.error('Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.outline }}>Laden...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Save bar */}
      {dirty && (
        <Box sx={{
          position: 'sticky', top: testbalkPlus(44), zIndex: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2.5, py: 1.25, mb: 3, borderRadius: '0.625rem',
          bgcolor: '#2d3335', color: '#fff',
          boxShadow: '0 4px 20px rgba(45,51,53,0.2)',
        }}>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 600 }}>
            Onopgeslagen wijzigingen
          </Typography>
          <Box
            onClick={handleSave}
            sx={{
              px: 2.5, py: 0.6, borderRadius: '0.375rem',
              bgcolor: 'rgba(255,255,255,0.15)', cursor: 'pointer',
              fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 700,
              transition: 'all 0.15s',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
              opacity: saving ? 0.5 : 1,
            }}
          >
            {saving ? 'Opslaan...' : 'Opslaan'}
          </Box>
        </Box>
      )}

      {/* AI-aanvulling uit publieke bronnen */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Box onClick={() => setAiOpen(true)} sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.7,
          borderRadius: '0.5rem', cursor: 'pointer', border: `1.5px solid ${T.outlineVariant}`,
          fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 700, color: T.primary,
          '&:hover': { borderColor: T.primary, bgcolor: T.surfaceLow },
        }}>
          <Icon name="auto_awesome" size={15} />
          Vul aan met AI (website)
        </Box>
      </Box>

      <Dialog open={aiOpen} onClose={() => !aiBusy && setAiOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: T.font, fontWeight: 800 }}>Aanvullen uit publieke bronnen</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.outline, mb: 2 }}>
            Leest de website van de klant (homepage + over-ons) en vult de lege velden
            als voorstel. Niets wordt opgeslagen tot jij op Opslaan klikt.
          </Typography>
          <TextField label="Website van de klant" fullWidth placeholder="bijv. tue.nl"
            value={aiWebsite} onChange={(e) => setAiWebsite(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && vulAanMetAI()} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAiOpen(false)} disabled={aiBusy}>Annuleren</Button>
          <Button variant="contained" onClick={vulAanMetAI} disabled={!aiWebsite.trim() || aiBusy}>
            {aiBusy ? 'Website lezen…' : 'Aanvullen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fields */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {PROFILE_FIELDS.map(field => (
          <Box key={field.key}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Icon name={field.icon} size={16} sx={{ color: T.outlineVariant }} />
              <Typography sx={{
                fontFamily: T.font, fontSize: '0.78rem', fontWeight: 700,
                color: T.text, letterSpacing: '-0.01em',
              }}>
                {field.label}
              </Typography>
            </Box>
            <TextField
              fullWidth
              multiline
              minRows={field.rows}
              maxRows={field.rows + 4}
              placeholder={field.placeholder}
              value={profile[field.key] || ''}
              onChange={(e) => handleChange(field.key, e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: T.fontBody, fontSize: '0.84rem', color: T.text,
                  borderRadius: '0.625rem', bgcolor: T.surface,
                  lineHeight: 1.65,
                  '& fieldset': { borderColor: 'rgba(45,51,53,0.08)' },
                  '&:hover fieldset': { borderColor: 'rgba(45,51,53,0.15)' },
                  '&.Mui-focused fieldset': { borderColor: T.primary, borderWidth: 1.5 },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: T.outlineVariant, opacity: 1, fontSize: '0.82rem',
                },
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ─── Overview Section ──────────────────────────────────────────────────────
function TeamMember({ icon, role, name }) {
  const naamTekst = alsTekst(name);
  if (!naamTekst.trim()) return null;
  // Split comma-separated names into individual chips
  const names = stripMd(naamTekst).split(',').map(n => n.trim()).filter(Boolean);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Icon name={icon} size={14} sx={{ color: T.outlineVariant }} />
        <Typography sx={{
          fontFamily: T.font, fontSize: '0.62rem', fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '0.08em', color: T.outlineVariant,
        }}>
          {role}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {names.map((n, i) => (
          <Box key={i} sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.75,
            px: 1.25, py: 0.5, borderRadius: '0.5rem',
            bgcolor: T.surfaceLow, border: '1px solid rgba(45,51,53,0.04)',
          }}>
            <Box sx={{
              width: 22, height: 22, borderRadius: '50%',
              background: `linear-gradient(135deg, ${T.primaryContainer} 0%, ${T.tertiaryContainer} 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.52rem', fontWeight: 800, color: T.primary,
              fontFamily: T.font,
            }}>
              {n.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()}
            </Box>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, color: T.text }}>
              {n}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function ProjectManagementBlock({ value: ruweValue }) {
  const value = alsTekst(ruweValue);
  if (!value.trim()) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Icon name="assignment" size={14} sx={{ color: T.outlineVariant }} />
        <Typography sx={{
          fontFamily: T.font, fontSize: '0.62rem', fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '0.08em', color: T.outlineVariant,
        }}>
          Projectmanagement
        </Typography>
      </Box>
      <Typography sx={{
        fontFamily: T.fontBody, fontSize: '0.78rem', color: T.text, lineHeight: 1.7,
        whiteSpace: 'pre-line',
        px: 1.5, py: 1.25, borderRadius: '0.5rem',
        bgcolor: T.surfaceLow, border: '1px solid rgba(45,51,53,0.04)',
      }}>
        {value.trim().split('\n').map((line, i, arr) => (
          <React.Fragment key={i}>{parseBold(line)}{i < arr.length - 1 && <br />}</React.Fragment>
        ))}
      </Typography>
    </Box>
  );
}

function OverviewSection({ account, accountId, onAccountGewijzigd }) {
  const navigate = useNavigate();
  const [teamOpen, setTeamOpen] = useState(false);

  const hasInfo = account?.accountmanager || account?.projectleiders || account?.projectmanagement || account?.preferredCrew;
  // Cijfers (budget, facturen, offertes) alleen met sales-rechten — de API eist dat ook.
  const { permissions } = useUserRole();
  const magCijfers = !!permissions?.sales_dashboard?.view;

  const sections = [
    ...(magCijfers ? [
      { id: 'cijfers', label: 'Cijfers', icon: 'bar_chart', desc: 'Budget, facturen, offertes en lopende projecten', color: T.primary, bg: T.primaryContainer },
    ] : []),
    // Relatiebeheer is voor iedereen (Joris, 26 sep); Cijfers blijft sales-only.
    { id: 'relatie', label: 'Relatiebeheer', icon: 'handshake', desc: 'Contactritme, activiteiten, tevredenheid en accountplan', color: T.primary, bg: T.primaryContainer },
    { id: 'profiel', label: 'Klantprofiel', icon: 'person_pin', desc: 'Missie, visie, doelgroep en tone of voice', color: '#10b981', bg: '#ecfdf5' },
    { id: 'huisstijl', label: 'Huisstijl', icon: 'palette', desc: "Logo's, kleuren, fonts en brand guidelines", color: '#3b82f6', bg: '#dbeafe' },
    { id: 'databeleid', label: 'Databeleid', icon: 'shield', desc: 'Privacy-rol, rechten en bewaartermijnen', color: '#0891b2', bg: '#cffafe' },
    { id: 'richtprijzen', label: 'Richtprijzen', icon: 'sell', desc: 'Wat de klant-assistent mag noemen', color: '#1f6f5c', bg: '#e2efea' },
    { id: 'playbook', label: 'AI Playbook', icon: 'auto_stories', desc: 'AI-gestuurde prompts per productiefase', color: T.tertiary, bg: T.tertiaryContainer },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Team & account info — altijd tonen, ook leeg: anders is er geen plek
          om de accountmanager alsnog in te vullen (Joris, 10 sep 2026). */}
      {(
        <Box sx={{
          p: 3, borderRadius: '0.75rem',
          bgcolor: T.surface,
          border: '1px solid rgba(45,51,53,0.06)',
          display: 'flex', flexDirection: 'column', gap: 2.5,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{
              fontFamily: T.font, fontSize: '0.62rem', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.08em', color: T.outlineVariant,
            }}>
              Team
            </Typography>
            <Tooltip title="Accountmanager, projectleiders en afspraken aanpassen">
              <Button size="small" startIcon={<Icon name="edit" size={15} />}
                onClick={() => setTeamOpen(true)}
                sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.72rem' }}>
                Bewerken
              </Button>
            </Tooltip>
          </Box>
          {!hasInfo && (
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.outlineVariant }}>
              Nog geen accountmanager of projectleiders ingevuld.
            </Typography>
          )}
          {/* Top row: AM + PL */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
            <TeamMember icon="badge" role="Accountmanager" name={account.accountmanager} />
            <TeamMember icon="supervisor_account" role="Projectleiders" name={account.projectleiders} />
          </Box>

          {/* Project management — parsed as key: value lines */}
          <ProjectManagementBlock value={account.projectmanagement} />

          {/* Preferred crew */}
          <TeamMember icon="groups" role="Preferred crew" name={account.preferredCrew} />
        </Box>
      )}
      {teamOpen && (
        <TeamBewerkDialog
          accountId={accountId}
          account={account}
          onClose={() => setTeamOpen(false)}
          onOpgeslagen={onAccountGewijzigd}
        />
      )}

      {/* Gekoppelde bedrijven */}
      {account?.bedrijvenDetails?.length > 0 && (
        <Box sx={{
          p: 3, borderRadius: '0.75rem',
          bgcolor: T.surface, border: '1px solid rgba(45,51,53,0.06)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
            <Icon name="domain" size={14} sx={{ color: T.outlineVariant }} />
            <Typography sx={{
              fontFamily: T.font, fontSize: '0.62rem', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.08em', color: T.outlineVariant,
            }}>
              Gekoppelde bedrijven
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {account.bedrijvenDetails.map((b) => {
              const logoUrl = b.logo?.thumbnails?.small?.url || b.logo?.url || null;
              return (
                // Klik = de projectlijst, gefilterd op dit bedrijf (wiki 27 sep: de chips deden niets).
                <Box key={b.id} component={RouterLink} to={klantProjectenLink(b.naam, b.id)}
                  title={`Projecten van ${b.naam}`}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 1,
                    px: 1.5, py: 0.6, borderRadius: '0.5rem', textDecoration: 'none',
                    bgcolor: T.surfaceLow, border: '1px solid rgba(45,51,53,0.04)',
                    '&:hover, &:focus-visible': { borderColor: 'rgba(45,51,53,0.18)' },
                  }}>
                  <LogoMetTerugval src={logoUrl} sx={{
                    width: 20, height: 20, borderRadius: '4px', objectFit: 'contain',
                    bgcolor: '#fff', flexShrink: 0,
                  }} fallback={(
                    <Box sx={{
                      width: 20, height: 20, borderRadius: '4px',
                      bgcolor: T.primaryContainer, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <Typography sx={{ fontSize: '0.48rem', fontWeight: 800, color: T.primary }}>
                        {b.naam.substring(0, 2).toUpperCase()}
                      </Typography>
                    </Box>
                  )} />
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, color: T.text }}>
                    {b.naam}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Section cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
        {sections.map(s => (
          <Box
            key={s.id}
            onClick={() => navigate(`?tab=${s.id}`, { replace: true })}
            sx={{
              display: 'flex', alignItems: 'center', gap: 2.5,
              bgcolor: T.surface, borderRadius: '0.75rem', p: 2.5,
              border: '1px solid rgba(45,51,53,0.06)',
              cursor: 'pointer', transition: 'all 0.15s',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(45,51,53,0.07)',
                borderColor: `${s.color}25`,
                transform: 'translateY(-2px)',
                '& .hub-arrow': { opacity: 1, transform: 'translateX(0)' },
              },
            }}
          >
            <Box sx={{ width: 44, height: 44, borderRadius: '0.625rem', bgcolor: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon name={s.icon} size={24} sx={{ color: s.color }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontFamily: T.font, fontSize: '0.92rem', fontWeight: 700, color: T.text, mb: 0.15 }}>
                {s.label}
              </Typography>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline, lineHeight: 1.45 }}>
                {s.desc}
              </Typography>
            </Box>
            <Box className="hub-arrow" sx={{ opacity: 0, transform: 'translateX(-4px)', transition: 'all 0.2s', flexShrink: 0 }}>
              <Icon name="arrow_forward" size={18} sx={{ color: s.color }} />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────
export default function AccountWorkspace() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { permissions, loading: rechtenLaden } = useUserRole();
  const magCijfers = !!permissions?.sales_dashboard?.view;

  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);

  const activeTab = searchParams.get('tab') || 'overview';
  const rawPhase = searchParams.get('phase');
  const activePhase = rawPhase || 'strategie';
  const isPlaybookPhase = activeTab === 'playbook' && !!rawPhase;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${BACKEND_URL}/Api/klanten/${accountId}`);
        if (res.ok) {
          const data = await res.json();
          const f = data.account?.fields || data.fields || data;
          if (!cancelled) setAccount({
            id: accountId,
            name: f.Account || f.Naam || f.name || 'Account',
            sector: f.Sector || '',
            logo: f.Beeldmerk?.[0]?.thumbnails?.small?.url || f.Beeldmerk?.[0]?.url || null,
            accountmanager: f.AccountmanagerNamen || f.Accountmanager || null,
            projectleiders: f.ProjectleidersNamen || f.Projectleiders || null,
            projectmanagement: f.Projectmanagment || null,
            preferredCrew: f['Preffered crew'] || null,
            // De koppelvelden leveren record-ids; die heeft het bewerkscherm nodig.
            accountmanagerIds: f.AccountmanagerIds || (Array.isArray(f.Accountmanager) ? f.Accountmanager : []),
            projectleiderIds: f.ProjectleiderIds || (Array.isArray(f.Projectleiders) ? f.Projectleiders : []),
            bedrijvenDetails: f.BedrijvenDetails || [],
            beleid: {
              rolFullFrame: f['Rol van Full Frame'] || null,
              eigendomsrecht: f['Eigendomsrecht'] || null,
              verwerkingsgebied: f['Verwerkinggebied beperkt tot'] || null,
              quitclaimVerantwoordelijke: f['Quitclaim-verantwoordelijke'] || null,
              quitclaimGebruikVan: f['Gebruik quitclaims van'] || null,
              retentieBepaaldVanuit: f['Dataretentiebeleid bepaald vanuit'] || null,
              retentieRuw: f['1. Ruw Beeldmateraal Bewaartermijn (in maanden)'] || null,
              retentieMasters: f['2. Masters & Quitclaims Bewaartermijn (in maanden)'] || null,
              retentieCompleet: f['3. Compleet Project Bewaartermijn (in maanden)'] || null,
              prioriteitVerwijdering: f['Prioriteit verwijdering'] || null,
              retentieExtra: f['Dataretentiebeleid (Extra informatie)'] || null,
            },
          });
        } else {
          const seed = SEED_ACCOUNTS.find(s => s.id === accountId);
          if (!cancelled) setAccount(seed || { id: accountId, name: accountId, sector: '' });
        }
      } catch {
        const seed = SEED_ACCOUNTS.find(s => s.id === accountId);
        if (!cancelled) setAccount(seed || { id: accountId, name: accountId, sector: '' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [accountId]);

  const accountName = account?.name || accountId;

  if (loading) {
    return (
      <Box sx={{ px: 4, pt: 6 }}>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.outline }}>Laden...</Typography>
      </Box>
    );
  }

  const bedrijvenCount = account?.bedrijvenDetails?.length || 0;

  return (
    <Box sx={{ width: '100%', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10 }}>

      {/* Sub-tab back link — playbook-fase terug naar de hub, overige terug naar overview */}
      {activeTab !== 'overview' && (
        <Box
          onClick={() => navigate(
            isPlaybookPhase
              ? `/accounts/${accountId}/workspace?tab=playbook`
              : `/accounts/${accountId}/workspace`,
            { replace: true }
          )}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.75, mb: 2,
            cursor: 'pointer',
            fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.72rem',
            textTransform: 'uppercase', letterSpacing: '0.08em',
            color: T.outline, transition: 'color 0.15s',
            '&:hover': { color: T.primary },
          }}
        >
          <Icon name="arrow_back" size={14} sx={{ color: 'inherit' }} />
          {isPlaybookPhase ? 'Terug naar AI Playbook' : `Terug naar ${accountName}`}
        </Box>
      )}

      {/* Shared header — PageHeader-look met account-logo en aantal bedrijven */}
      <PageHeader
        logoUrl={account?.logo}
        fallbackText={accountName.substring(0, 2).toUpperCase()}
        title={accountName}
        subtitle={`${bedrijvenCount} ${bedrijvenCount === 1 ? 'bedrijf' : 'bedrijven'}`}
      />

      {/* Content — driven by ?tab= */}
      {activeTab === 'overview' && (
        <OverviewSection account={account} accountId={accountId}
          onAccountGewijzigd={(rec) => {
            const f = rec?.fields;
            if (!f) return;
            setAccount((huidig) => ({
              ...huidig,
              accountmanager: f.AccountmanagerNamen || f.Accountmanager || null,
              projectleiders: f.ProjectleidersNamen || f.Projectleiders || null,
              projectmanagement: f.Projectmanagment || null,
              preferredCrew: f['Preffered crew'] || null,
              accountmanagerIds: f.AccountmanagerIds || (Array.isArray(f.Accountmanager) ? f.Accountmanager : []),
              projectleiderIds: f.ProjectleiderIds || (Array.isArray(f.Projectleiders) ? f.Projectleiders : []),
            }));
          }} />
      )}

      {/* Cijfers: alleen met sales-rechten (de API eist dat ook). */}
      {activeTab === 'cijfers' && !rechtenLaden && !magCijfers && (
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.outline }}>
          Dit onderdeel is alleen zichtbaar met sales-rechten.
        </Typography>
      )}

      {activeTab === 'cijfers' && magCijfers && (
        <AccountCijfersSection accountId={accountId} />
      )}

      {activeTab === 'relatie' && (
        <Box>
          <AccountCrmSection accountId={accountId} accountName={accountName}
            defaultAccountmanager={alsTekst(account?.accountmanager).split(',')[0].trim() || null} />
          <AccountSatisfactionSection accountName={accountName} />
          <AccountPlanSection accountId={accountId} />
        </Box>
      )}

      {activeTab === 'profiel' && (
        <KlantProfielSection accountId={accountId} />
      )}

      {activeTab === 'huisstijl' && (
        <KlantDetail embedded bedrijfId={accountId} />
      )}

      {activeTab === 'databeleid' && (
        <DatabeleidSection beleid={account?.beleid} />
      )}

      {activeTab === 'richtprijzen' && (
        <AccountPriceGuidanceSection accountId={accountId} accountName={accountName} />
      )}

      {activeTab === 'playbook' && (
        isPlaybookPhase
          ? <PlaybookSection accountName={accountName} activePhase={activePhase} />
          : <PlaybookHub />
      )}
    </Box>
  );
}
